import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ShareLook } from "@/components/ShareLook";
import { HEATHER_IMG, HEATHER_NAME, HEATHER_TITLE, STORE_ADDRESS, STORE_PHONE, STORE_HOURS, LOGO_H } from "@/lib/brand";
import { Loader2, Check, Scissors, MapPin, Phone, Clock, Sparkles, Heart, Calendar, ShieldCheck, ArrowRight } from "lucide-react";
import { mpPurchase } from "@/lib/metaPixel";

const CONFETTI_COLORS = ["#F7E7CE", "#B76E79", "#F5E6E8", "#FAF9F6", "#D4AF37"];

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 4,
    duration: 4 + Math.random() * 4,
    size: 5 + Math.random() * 7,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: Math.random() * 360,
    round: Math.random() > 0.5,
  })), []);
  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" aria-hidden data-testid="thankyou-confetti">
      <style>{`@keyframes hb-confetti-fall { 0% { transform: translateY(-6vh) rotate(0deg); opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg); opacity: 0; } }`}</style>
      {pieces.map((p, i) => (
        <span key={i} style={{
          position: "absolute", top: "-6vh", left: `${p.left}%`, width: p.size, height: p.size * (p.round ? 1 : 0.45),
          backgroundColor: p.color, borderRadius: p.round ? "50%" : "1px", transform: `rotate(${p.rotate}deg)`,
          animation: `hb-confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

const HEATHER_TIPS = [
  "Book your fitting about 3 weeks before your event — it gives us time to make every seam perfect.",
  "Hang your dress the moment it arrives and let it breathe. Steam, never iron.",
  "Bring the shoes you'll actually wear to your fitting — hem length changes everything.",
  "Take a photo in natural light before the big night. You'll want to remember exactly how you felt.",
];

export default function ThankYou() {
  const { orderId } = useParams();
  const { refresh: refreshCart } = useCart();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    api.orderReceipt(orderId).then(({ data }) => {
      setData(data);
      // Meta Pixel — Purchase (fire once per order; dedupe key = orderId)
      try {
        const key = `hb_meta_purchase_${orderId}`;
        if (typeof window !== "undefined" && !localStorage.getItem(key)) {
          const order = data?.order || {};
          const items = (order.items || []).map((it) => ({
            id: it.color ? `${it.product_id}:${it.color}` : it.product_id,
            quantity: it.quantity || 1,
            item_price: it.price || 0,
          }));
          mpPurchase({
            orderId: order.id,
            value: order.total || 0,
            items,
            kind: order.kind,
          });
          localStorage.setItem(key, "1");
        }
      } catch { /* noop */ }
    }).catch(() => setErr(true));
    refreshCart?.();
  }, [orderId]);

  if (err) return (
    <div className="h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="font-display text-3xl text-muted-foreground">We couldn't find that order.</p>
      <Link to="/shop" className="bg-ink text-ivory px-8 py-3.5 font-label text-[10px] rounded-sm">Back to the collection</Link>
    </div>
  );
  if (!data) return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  const { order, product, booking, school_name, first_name, tryon_url } = data;
  const item = (order.items || [])[0] || {};
  const tryonImg = tryon_url ? `${process.env.REACT_APP_BACKEND_URL}${tryon_url}` : null;
  const dressImg = item.image || product?.images?.[0];
  const img = tryonImg || dressImg;
  const isReserve = order.kind === "reserve";

  return (
    <div data-testid="thankyou-page">
      <Confetti />

      {/* HERO */}
      <section className="relative bg-ink text-ivory overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F7E7CE 1px, transparent 1px), radial-gradient(circle at 70% 60%, #F7E7CE 1px, transparent 1px)", backgroundSize: "90px 90px, 130px 130px" }} />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-20 sm:py-24 text-center">
          <img src={LOGO_H} alt="Heather's" className="w-14 h-14 mx-auto rounded-full object-cover mb-6 border border-champagne/40" />
          <p className="font-label text-[10px] text-champagne mb-4 flex items-center justify-center gap-2"><Check className="w-3.5 h-3.5" /> Payment confirmed · Order #{order.id.slice(0, 8).toUpperCase()}</p>
          <h1 data-testid="thankyou-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">You did it, {first_name}.<br /><span className="text-champagne italic">This night is officially yours.</span></h1>
          <p className="font-body text-base text-ivory/70 mt-6 max-w-xl mx-auto">
            {isReserve
              ? <>The <b className="text-ivory">{item.name}</b> is off the market{school_name ? <> at <b className="text-ivory">{school_name}</b></> : ""} — one-of-one, locked forever, no one else will wear it. That's not shopping. That's a statement.</>
              : <>Your order is confirmed and Heather is already on it. Every piece will be steamed, pressed and ready for your moment.</>}
          </p>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 -mt-10 relative z-10 pb-24">
        <div className="grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6">
          {/* ORDER CARD */}
          <div data-testid="thankyou-order-card" className="bg-ivory border border-border rounded-sm shadow-xl overflow-hidden">
            {img && (
              <div className="relative aspect-[4/5] overflow-hidden bg-blush">
                <img src={img} alt={item.name} className="w-full h-full object-cover object-top" />
                {tryonImg && <span data-testid="thankyou-tryon-badge" className="absolute top-3 left-3 glass-dark text-ivory font-label text-[9px] px-3 py-1.5 rounded-full flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-champagne" /> You in it — AI Try-On</span>}
              </div>
            )}
            <div className="p-6">
              <p className="font-label text-[9px] text-rosemetal mb-1">Your reservation</p>
              <h3 className="font-display text-2xl leading-tight">{item.name}</h3>
              <div className="mt-4 space-y-2 font-body text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{item.name}{item.color ? ` · ${item.color}` : ""}{item.size ? ` · Size ${item.size}` : ""}</span><span>${item.price}</span></div>
                {booking && <div className="flex justify-between"><span className="text-muted-foreground">Master Seamstress Suite</span><span>${booking.fee}</span></div>}
                <div className="flex justify-between font-display text-xl border-t border-border pt-3 mt-3"><span>Total paid</span><span>${order.total.toFixed(2)}</span></div>
              </div>
              <p className="font-label text-[8px] text-muted-foreground mt-4 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Paid securely · A text confirmation is on its way</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* HEATHER'S NOTE */}
            <div data-testid="thankyou-heather-note" className="bg-blush/60 border border-rosemetal/20 rounded-sm p-7">
              <div className="flex items-center gap-3 mb-4">
                <img src={HEATHER_IMG} alt={HEATHER_NAME} className="w-14 h-14 rounded-full object-cover object-top border-2 border-ivory shadow" />
                <div><div className="font-display text-xl">{HEATHER_NAME}</div><div className="font-label text-[8px] text-muted-foreground">{HEATHER_TITLE}</div></div>
              </div>
              <p className="font-display text-2xl leading-snug italic">"{first_name}, out of 2,000 dresses, this one found <span className="text-rosemetal">you</span> — and I couldn't have picked better myself. You're going to walk in and the whole room will know it. I'm personally making sure every detail is perfect for your night."</p>
              <p className="font-label text-[9px] text-rosemetal mt-4 flex items-center gap-1.5"><Heart className="w-3 h-3 fill-rosemetal" /> With love, Heather</p>
            </div>

            {/* APPOINTMENT */}
            {booking && (
              <div data-testid="thankyou-appointment" className="bg-ivory border border-rosemetal/40 rounded-sm p-6">
                <p className="font-label text-[9px] text-rosemetal mb-3 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5" /> Your private fitting with Heather</p>
                <p className="font-display text-2xl flex items-center gap-2"><Calendar className="w-5 h-5 text-rosemetal" /> {booking.appointment ? new Date(booking.appointment).toLocaleString([], { dateStyle: "full", timeStyle: "short" }) : "Heather will text you to schedule"}</p>
                <div className="mt-3 space-y-1 font-body text-xs text-muted-foreground">
                  <p className="flex items-center gap-2"><MapPin className="w-3 h-3 text-rosemetal" /> {STORE_ADDRESS}</p>
                  <p className="flex items-center gap-2"><Phone className="w-3 h-3 text-rosemetal" /> {STORE_PHONE}</p>
                  <p className="flex items-center gap-2"><Clock className="w-3 h-3 text-rosemetal" /> {STORE_HOURS}</p>
                </div>
              </div>
            )}

            {/* SHARE */}
            {isReserve && (
              <div className="bg-ink text-ivory rounded-sm p-6">
                <p className="font-label text-[9px] text-champagne mb-2">Make it official</p>
                <p className="font-display text-2xl leading-snug mb-4">Post your locked look before someone hears it from someone else.</p>
                <button data-testid="thankyou-share-btn" onClick={() => setShowShare(true)} className="w-full bg-champagne text-ink py-3.5 font-label text-[10px] rounded-sm hover:bg-ivory transition-colors flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" /> Share My Locked Look</button>
              </div>
            )}

            {/* TIPS */}
            <div data-testid="thankyou-tips" className="border border-border rounded-sm p-6">
              <p className="font-label text-[9px] text-muted-foreground mb-4">Heather's insider tips for your night</p>
              <ul className="space-y-3">
                {HEATHER_TIPS.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 font-body text-sm text-muted-foreground"><Check className="w-4 h-4 text-rosemetal mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>

            <Link to="/shop" data-testid="thankyou-continue-btn" className="w-full border border-ink py-4 font-label text-[10px] rounded-sm hover:bg-ink hover:text-ivory transition-colors flex items-center justify-center gap-2">Find Your Accessories <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </div>

      {product && (
        <ShareLook
          productId={product.id}
          open={showShare}
          onClose={() => setShowShare(false)}
          imageUrl={img}
          productName={item.name}
          schoolName={school_name}
          productUrl={`${window.location.origin}/locked/${product.id}`}
          remainingLabel="FOREVER"
        />
      )}
    </div>
  );
}
